import React, {useEffect, useMemo} from 'react'
import {useSelector} from 'react-redux'
import {CompactOffer} from 'components/Offer/Compact'
import {getOfferVariant} from 'components/Offer/Shared/utils'
import {useOfferContainer} from 'hooks/useOfferContainer'
import {
  getAnchorHotelInDaedalusFormat,
  getHotelsInDaedalusFormat,
  getHotelTopOffer,
  getModuleState,
  getOfferEntity,
  getSplitBookingDetails
} from 'modules/sapiSearch/selectors'
import {getSearchParameters} from 'modules/sapiSearch/selectors/searchParameters'
import {ModuleState as SapiModuleState} from 'modules/sapiSearch/slice'
import {getError} from 'modules/search/selectors'
import {HotelType} from 'types/Hotel'

import {useDeviceLayout} from '@vio/atlas/context/deviceLayout'
import {PopUp} from '@vio/atlas/PopUp'
import {useBrandCapabilities} from '@vio/core/src/_web/brand/hooks/useBrandCapabilities'
import {selectIsAuthenticated} from '@vio/core/src/auth/modules/selectors'
import {useToggle} from '@vio/core/src/experiments/hooks/useToggle'
import getIsBofhOffer from '@vio/core/src/offer/business/isBofhOffer'
import {useFreezeLimits} from '@vio/core/src/offer/services/offerConfigurationApi/useFreezeLimits'
import {useDealFreezeEnabled} from '@vio/shared/src/dealFreeze/hooks/useDealFreezeEnabled'
import {
  getActiveFrozenBookingCount,
  getFrozenBookingByItinerary
} from '@vio/shared/src/dealFreeze/modules/selector'

import {DealFreezeExitPopup} from './DealFreezeExitPopup'
import {ExitPopupContent} from './ExitPopupContent'
import {ExitPopupHeader} from './ExitPopupHeader'
import {ExitPopupSkeleton} from './ExitPopupSkeleton'
import {MODAL_WIDTH, PopupOfferContainer} from './styles'
import {useExitPopup} from './useExitPopup'
import {
  getHotelWithCheapestVisibleFreezableOffer,
  getHotelWithCheapestVisibleTopOffer
} from './utils'

const DEAL_FREEZE_EXIT_POPUP_TOGGLE = 'c9a3a3ec-deal-freeze-exit-popup'

export const ExitPopupSearchPage: React.FC = () => {
  const toggle = useToggle()
  const {isMobile} = useDeviceLayout()
  const {
    monetisation: {cashback}
  } = useBrandCapabilities()

  const isAuthenticated = useSelector(selectIsAuthenticated)
  const searchError = useSelector(getError)
  const moduleState = useSelector(getModuleState)
  const searchIsFinished = moduleState === SapiModuleState.SearchCompleted

  const anchorHotel = useSelector(
    getAnchorHotelInDaedalusFormat
  ) as unknown as HotelType

  const paginatedResults = useSelector(
    getHotelsInDaedalusFormat
  ) as unknown as HotelType[]

  const searchHasError = Boolean(searchError)

  const isAnchorWithOffers = (hotel: HotelType) =>
    hotel?.offers?.length > 0 && hotel?.topOfferData

  const hotelsList: HotelType[] = useMemo(() => {
    if (isAnchorWithOffers(anchorHotel)) {
      if (anchorHotel) return [anchorHotel]
      return []
    }
    return paginatedResults
  }, [anchorHotel, paginatedResults])

  // Standard popup target hotel (cheapest visible top offer).
  const hotelDetails = useMemo(() => {
    return getHotelWithCheapestVisibleTopOffer(hotelsList, isAuthenticated)
  }, [hotelsList, isAuthenticated])

  // Deal Freeze variant target hotel (cheapest visible freezable offer).
  const dfHotel = useMemo(() => {
    return getHotelWithCheapestVisibleFreezableOffer(
      hotelsList,
      isAuthenticated
    )
  }, [hotelsList, isAuthenticated])

  // ─── Deal Freeze A/B test eligibility ───
  // Compute preconditions before consulting the toggle so we only bucket users
  // who could actually see the DF variant.
  const dfBrandEnabled = useDealFreezeEnabled()
  const {checkIn: searchCheckIn, checkOut: searchCheckOut} =
    useSelector(getSearchParameters)
  const sameItineraryFrozen = useSelector(state =>
    Boolean(
      getFrozenBookingByItinerary(
        state,
        searchCheckIn,
        searchCheckOut,
        dfHotel?.objectID
      )
    )
  )
  const activeFrozenBookingCount = useSelector(getActiveFrozenBookingCount)
  const {atOnce} = useFreezeLimits()
  const isAtMaxFreezes = activeFrozenBookingCount >= atOnce
  const isDfEligible =
    dfBrandEnabled &&
    Boolean(dfHotel) &&
    !sameItineraryFrozen &&
    !isAtMaxFreezes

  // Only fires Opticks bucketing for the DF-eligible cohort. Both arms of the
  // experiment are drawn from the same eligible audience.
  const isDfVariant =
    isDfEligible && toggle(DEAL_FREEZE_EXIT_POPUP_TOGGLE, false, true)

  const {closePopup, dismissPopup, isOpen} = useExitPopup({
    page: 'Search',
    isEnabled: searchIsFinished,
    bypassAuthAndDirectMobile: isDfVariant,
    variant: isDfVariant ? 'deal_freeze' : 'standard'
  })

  const hotelId = hotelDetails?.objectID

  const offerEntity = useSelector(state => getOfferEntity(state, hotelId))

  const offer = useSelector(state => getHotelTopOffer(state, hotelId))

  const {hasSplitBookingOffer, splitBookingOffers} = useSelector(state =>
    getSplitBookingDetails(state, hotelId)
  )

  const offerIndex = 0 //  We always use first Item of offers list

  const isCheapest = true
  const isBofhOffer = getIsBofhOffer(offer)

  const {anchorPrice, anchorPriceTotal, isPrimaryOfferGreatDeal} =
    useOfferContainer({
      offerEntity,
      hotelId,
      withGreatDealTag: true
    })

  const sourceComponent = useMemo(
    () => (isMobile ? 'MobileBestDealModal' : 'DesktopBestDealModal'),
    [isMobile]
  )

  useEffect(() => {
    if (isOpen) {
      // For the DF variant: close if the freezable hotel disappears.
      // For control: close if the cheapest hotel disappears.
      const targetMissing = isDfVariant ? !dfHotel : !hotelDetails
      if ((targetMissing && searchIsFinished) || searchHasError) {
        closePopup()
      }
    }
  }, [
    hotelDetails,
    dfHotel,
    isDfVariant,
    searchIsFinished,
    searchHasError,
    isOpen,
    closePopup
  ])

  if (!isOpen) return null

  // Deal Freeze variant render.
  if (isDfVariant && dfHotel) {
    const dfOfferIndex = dfHotel.topOfferData?.offerIndexes?.[0] ?? 0
    const dfOffer = dfHotel.offers?.[dfOfferIndex]
    if (!dfOffer) return null
    return (
      <DealFreezeExitPopup
        hotel={dfHotel}
        offer={dfOffer}
        closePopup={closePopup}
        dismissPopup={dismissPopup}
      />
    )
  }

  // Showing loading state
  if (!hotelDetails)
    return (
      <PopUp
        width={MODAL_WIDTH}
        isOpen={isOpen}
        closeAction={dismissPopup}
        navBarProps={{
          centerContent: (
            <ExitPopupHeader variant="loading" isMobile={isMobile} />
          ),
          centerAligned: true,
          variant: 'inverse'
        }}
      >
        <ExitPopupSkeleton />
      </PopUp>
    )

  return (
    <PopUp
      width={MODAL_WIDTH}
      isOpen={isOpen}
      noPaddings
      navBarProps={{
        centerContent: (
          <ExitPopupHeader variant="searchResultsPage" isMobile={isMobile} />
        ),
        centerAligned: true,
        variant: 'inverse'
      }}
      closeAction={dismissPopup}
      footer={
        <PopupOfferContainer>
          <CompactOffer
            {...(hasSplitBookingOffer
              ? {
                  isSplitBooking: true,
                  variant: isMobile
                    ? 'CompactWithExpandableTag'
                    : 'CompactDefault',
                  sourceComponent,
                  splitBookingOffers,
                  hasRoomLabels: true,
                  anchorPrice,
                  anchorPriceTotal,
                  hotelId
                }
              : {
                  hasInteraction: true,
                  offerPosition: offerIndex,
                  variant: isMobile
                    ? 'CompactWithExpandableTag'
                    : 'CompactDefault',
                  sourceComponent,
                  offerVariant: getOfferVariant({
                    isCheapest,
                    isBofhOffer,
                    isGreatDealOffer: isPrimaryOfferGreatDeal
                  }),
                  hasRoomLabels: true,
                  anchorPrice,
                  anchorPriceTotal,
                  offer,
                  hotelId,
                  showCashback: cashback.enabled
                })}
          />
        </PopupOfferContainer>
      }
    >
      <ExitPopupContent
        hotel={hotelDetails}
        paddingTop={isMobile ? 's500' : 's300'}
        paddingRight={isMobile ? 's400' : 's500'}
        paddingLeft={isMobile ? 's400' : 's500'}
        paddingBottom={isMobile ? 's400' : 's600'}
      />
    </PopUp>
  )
}
